import { encounterCreatureField, computedValue } from '../../shared/array-types';
import { readPlayerCharacterField } from '../display/player-character-field';
import { essenceTotal } from '../spells/spell-prep-limits';
import { readItemField } from '../compile/item-field-reader';

type Row = Record<string, unknown>;
const int = (value: unknown): number => Math.trunc(Number(value ?? 0));

/** Check the final storage plan before the producer sends any browser-decided totals. */
function requireNumericPlan(integers: readonly number[], numbers: readonly number[], reason: string): void {
    if (!integers.every(Number.isSafeInteger) || !numbers.every(Number.isFinite)) throw new Error(reason);
}

/** A copy starts healthy; source statuses are deliberately not copied. */
export function characterCopyVitals(caster: Row): { v_max: number; spirit: number } {
    const override = int(readPlayerCharacterField(caster, 'v_max_override'));
    const maximum = computedValue(caster, 'vitality', 'v_max');
    if ((override <= 0 && maximum == null) || (computedValue(caster, 'creature', 'essences') == null
        && computedValue(caster, 'essences', 'spirit') == null)) throw new Error('The character totals could not be loaded.');
    const vMax = override > 0 ? override : int(maximum);
    const spirit = essenceTotal(caster, 'spirit');
    const initial = { v_max: vMax, spirit };
    requireNumericPlan(Object.values(initial), [], 'The character totals could not be loaded.');
    return initial;
}

/** Reinforcement leaves shed piles intact and heals only the newcomers' rounded share. */
export function planSwarmReinforcement(caster: Row, state: Row | null, count: number | 'full') {
    const ec = caster.encounter_creatures;
    const own = (key: string): unknown => encounterCreatureField(ec, key);
    const saved = (key: string): unknown => state ? readItemField(state, 'encounter_creature_swarm_state_' + key) : null;
    if (int(own('encounter_creature_dead')) || int(own('encounter_creature_deleted')))
        throw new Error('This swarm is dead — resurrect it first.');
    if (!state) {
        if (int(own('swarm_members_start')) > 0) throw new Error('This swarm is already at full strength.');
        throw new Error('Only a swarm can be reinforced.');
    }
    if (count !== 'full' && (!Number.isInteger(count) || count < 1)) throw new Error('Choose how many members to add.');
    const members = Math.max(0, int(saved('members'))), start = Math.max(1, int(saved('members_start')));
    const target = count === 'full' ? start : Math.min(start, members + Math.min(200, count));
    if (target <= members) throw new Error('This swarm is already at full strength.');
    const current = int(own('v_current')), maximum = int(own('v_max'));
    const rawNotes = saved('notes');
    let notes: Row | null = null;
    try { notes = typeof rawNotes === 'string' && rawNotes !== '' ? JSON.parse(rawNotes) : null; }
    catch { /* Older saved rows can lack a usable basis; reconstruct it from their vitality and count. */ }
    const pinned = Number(readItemField(notes, 'v_start') ?? 0);
    const vStart = pinned > 0 ? pinned : Math.max(1, maximum * start / Math.max(1, members));
    const newMaximum = Math.ceil(vStart * target / start - 1e-9);
    const heal = newMaximum - Math.ceil(vStart * members / start - 1e-9);
    const after = { members: target, v_start: vStart, v_max: newMaximum, v_current: Math.min(newMaximum, current + Math.max(0, heal)) };
    const before = { state_revision: int(saved('revision')), v_current: current, v_max: maximum };
    requireNumericPlan([...Object.values(before), after.members, after.v_max, after.v_current], [after.v_start],
        'The swarm totals could not be loaded.');
    return { before, after };
}
